/**
 * Structured logging helper that forwards to Sentry Logs in production
 * and mirrors to the console in development for easy local debugging.
 *
 * Usage:
 *   import { log } from "@/lib/log";
 *   log.info("credential synced", { user_id, credential_id });
 *   log.error("sync failed", { user_id, error: err.message });
 */
import { Sentry } from "./sentry";

type Attrs = Record<string, unknown>;

const isProd = import.meta.env.PROD;

function send(
  level: "info" | "warn" | "error" | "debug",
  message: string,
  attrs?: Attrs
) {
  // Always mirror to console in dev so we don't lose visibility locally.
  if (!isProd) {
    const fn =
      level === "error"
        ? console.error
        : level === "warn"
          ? console.warn
          : console.log;
    if (attrs) fn(`[${level}] ${message}`, attrs);
    else fn(`[${level}] ${message}`);
    return;
  }

  // Production: forward to Sentry Logs (requires _experiments.enableLogs).
  try {
    const logger = (Sentry as unknown as { logger?: Record<string, (msg: string, attrs?: Attrs) => void> }).logger;
    if (logger && typeof logger[level] === "function") {
      logger[level](message, attrs);
      return;
    }
  } catch {
    // ignore — fall through to console
  }
  // Fallback if Sentry logger isn't available
  if (level === "error" || level === "warn") {
    (level === "error" ? console.error : console.warn)(message, attrs);
  }
}

export const log = {
  debug: (msg: string, attrs?: Attrs) => send("debug", msg, attrs),
  info: (msg: string, attrs?: Attrs) => send("info", msg, attrs),
  warn: (msg: string, attrs?: Attrs) => send("warn", msg, attrs),
  error: (msg: string, attrs?: Attrs) => send("error", msg, attrs),
};
